/**
 * IR-1548: the prod and dev folders for one session, side by side.
 *
 * A single-environment listing cannot answer the question an investigator has.
 * "412 media files, 0 B" is ambiguous on its own — a group with no media looks
 * exactly like media that never arrived. Put dev beside it holding the same 412
 * files at 38 MB and the ambiguity is gone: that is delivery loss.
 *
 * So this panel's subject is DIVERGENCE, not inventory. Kinds that agree recede
 * to muted text; the kind that differs is lifted, because it is the finding.
 *
 * ⚠️ IR-1441 r5, learned the hard way on the Avatars expander: this renders
 * inside the sessions table's HORIZONTAL SCROLLER, so its cell is as wide as
 * every column combined. Anything pushed right by a flex spacer ends up off
 * screen — there, a delivery pill sat roughly a thousand pixels past the window
 * edge, invisible in production screenshots while looking correct locally.
 * `position: sticky; left: 0` with a width cap pins the content to the
 * SCROLLPORT's left edge rather than the table's.
 */
export type FolderObject = { name: string; path: string; size: number | null };

export type KindName = "chat" | "metadata" | "members" | "avatar" | "media";

export type KindRoll = {
  count: number;
  bytes: number;
  /** Files whose size GCS did not report. Not zero — unknown. Counting them as
   *  zero would manufacture the all-empty finding this panel exists to make. */
  unknownSizes: number;
};

export type Roll = Record<KindName, KindRoll>;

const KIND_ORDER: KindName[] = ["chat", "metadata", "members", "avatar", "media"];

const KIND_LABEL: Record<KindName, string> = {
  chat: "Chat",
  metadata: "Metadata",
  members: "Members",
  avatar: "Avatar",
  media: "Media",
};

/**
 * Both chat filenames are matched. Two scraper generations write different
 * ones — `group_chats.json` (V2) and `group_chat.json` (V1) — and matching only
 * the singular silently classified every V2 folder's chat as something else.
 */
function classify(name: string): KindName | null {
  const base = name.split("/").pop() ?? name;
  if (base === "group_chat.json" || base === "group_chats.json") return "chat";
  if (base === "group_members.json") return "members";
  if (base === "group_info.json" || base === "group_links.json") return "metadata";
  if (base === "group_avatar.jpg" || base.startsWith("group_profile_photo")) return "avatar";
  if (name.startsWith("users/") || name.startsWith("media/")) return "media";
  return null;
}

export function rollUp(objects: FolderObject[]): Roll {
  const empty = (): KindRoll => ({ count: 0, bytes: 0, unknownSizes: 0 });
  const roll = {
    chat: empty(),
    metadata: empty(),
    members: empty(),
    avatar: empty(),
    media: empty(),
  } satisfies Roll;

  for (const o of objects) {
    const kind = classify(o.name);
    if (!kind) continue;
    const k = roll[kind];
    k.count += 1;
    if (o.size == null) k.unknownSizes += 1;
    else k.bytes += o.size;
  }
  return roll;
}

function bytes(n: number): string {
  if (n === 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Two sides of one kind agree when the file count AND the byte total match. */
function diverges(a: KindRoll | null, b: KindRoll | null): boolean {
  if (!a || !b) return true;
  return a.count !== b.count || a.bytes !== b.bytes;
}

function Cell({ roll }: { roll: KindRoll | null }) {
  // An absent side is announced once above the grid, so the cells themselves
  // stay quiet rather than repeating it per kind.
  if (!roll) {
    return <div className="text-[var(--iris-fg-5)] text-xs">—</div>;
  }
  if (roll.count === 0) {
    return <div className="text-[var(--iris-fg-4)] text-xs">none</div>;
  }
  return (
    <div className="text-xs">
      <span className="text-[var(--iris-fg-1)]">
        {roll.count} {roll.count === 1 ? "file" : "files"}
      </span>
      <span className="text-[var(--iris-fg-3)]"> · {bytes(roll.bytes)}</span>
      {roll.unknownSizes > 0 && (
        <span className="text-[var(--iris-fg-4)]"> · {roll.unknownSizes} size unknown</span>
      )}
    </div>
  );
}

export function SessionFolderCompare({
  cycleId,
  prod,
  dev,
}: {
  cycleId: string;
  /** null means that environment has no session for this cycle at all. */
  prod: FolderObject[] | null;
  dev: FolderObject[] | null;
}) {
  const pRoll = prod ? rollUp(prod) : null;
  const dRoll = dev ? rollUp(dev) : null;

  // The loud case: files are present but every one of them is empty. A count
  // with no bytes is delivery loss, and it must not read as a cosmetic warning.
  const emptyMedia = (roll: KindRoll | null) =>
    roll && roll.count > 0 && roll.bytes === 0 && roll.unknownSizes === 0;

  return (
    <div
      className="px-9 py-4"
      style={{
        position: "sticky",
        left: 0,
        width: "min(100%, calc(100vw - 220px))",
      }}
    >
      <div className="text-xs text-[var(--iris-fg-3)] mb-2 font-mono">{cycleId}</div>

      {/* A whole side missing is ONE fact about the session, not five facts
          about its file kinds. Repeating it per row buried the finding in its
          own noise — and it is the gap the interleaved sort exists to surface,
          so it should read as a headline. */}
      {(!pRoll || !dRoll) && (
        <div className="text-xs text-[var(--iris-neon-orange)] mb-2">
          {!pRoll ? "not collected in prod" : "not collected in dev"} — this session ran in{" "}
          {pRoll ? "prod" : "dev"} only
        </div>
      )}

      <div
        className="grid gap-px bg-[var(--iris-line-1)] border border-[var(--iris-line-2)]"
        style={{ gridTemplateColumns: "96px 1fr 1fr" }}
      >
        <div className="bg-[var(--iris-bg-3)] px-3 py-1.5" />
        <div className="bg-[var(--iris-bg-3)] px-3 py-1.5 text-[10px] tracking-widest text-[var(--iris-fg-3)]">
          PROD
        </div>
        <div className="bg-[var(--iris-bg-3)] px-3 py-1.5 text-[10px] tracking-widest text-[var(--iris-fg-3)]">
          DEV
        </div>

        {KIND_ORDER.map((kind) => {
          const p = pRoll?.[kind] ?? null;
          const d = dRoll?.[kind] ?? null;
          const differs = diverges(pRoll ? p : null, dRoll ? d : null);
          return (
            <Row
              key={kind}
              kind={kind}
              p={p}
              d={d}
              differs={differs}
              hasProd={!!pRoll}
              hasDev={!!dRoll}
            />
          );
        })}
      </div>

      {emptyMedia(pRoll?.media ?? null) && (
        <Alarm side="prod" count={pRoll?.media.count ?? 0} other={dRoll?.media ?? null} />
      )}
      {emptyMedia(dRoll?.media ?? null) && (
        <Alarm side="dev" count={dRoll?.media.count ?? 0} other={pRoll?.media ?? null} />
      )}
    </div>
  );
}

function Row({
  kind,
  p,
  d,
  differs,
  hasProd,
  hasDev,
}: {
  kind: KindName;
  p: KindRoll | null;
  d: KindRoll | null;
  differs: boolean;
  hasProd: boolean;
  hasDev: boolean;
}) {
  // `data-diverges` is the hook the tests assert on, and the styling follows
  // it — so a row can never LOOK divergent while reporting that it agrees.
  const cell = differs ? "bg-[var(--iris-bg-3)]" : "bg-[var(--iris-bg-1)]";
  return (
    <>
      <div className="bg-[var(--iris-bg-2)] px-3 py-2 text-[11px] uppercase tracking-wide text-[var(--iris-fg-4)] flex items-center">
        {KIND_LABEL[kind]}
      </div>
      <div className={`${cell} px-3 py-2`} data-diverges={differs ? "true" : "false"}>
        <Cell roll={hasProd ? p : null} />
      </div>
      <div className={`${cell} px-3 py-2`} data-diverges={differs ? "true" : "false"}>
        <Cell roll={hasDev ? d : null} />
      </div>
    </>
  );
}

function Alarm({
  side,
  count,
  other,
}: {
  side: "prod" | "dev";
  count: number;
  other: KindRoll | null;
}) {
  const otherHas = other && other.count > 0 && other.bytes > 0;
  return (
    <div
      className="mt-3 flex gap-3 border border-[rgba(255,51,85,0.34)] bg-[rgba(255,51,85,0.055)] px-3 py-2"
      style={{ borderLeftWidth: 3, borderLeftColor: "var(--iris-neon-red)" }}
    >
      <div>
        <div className="text-[var(--iris-neon-red)] text-xs font-semibold">
          {count} of {count} media objects are 0 B in {side}
          {otherHas ? ` — the other environment has all ${other?.count}` : ""}
        </div>
        <div className="text-[var(--iris-fg-3)] text-xs mt-0.5">
          {otherHas
            ? "Same session, same folder. The media reached one environment and not the other, so this is delivery loss rather than a group with no media."
            : "Files are listed but every one is empty. Sizes come from GCS metadata, so this is measured rather than missing."}
        </div>
      </div>
    </div>
  );
}
